import { useEffect, useRef, useState, type FormEvent } from "react";
import { connectFinalFew, ffRequest, pounds, syncFinalFewClock, type FFSocket } from "@unfaithful/client";
import { LADDER, RULES, type FFPlayerView, type GameMode, type PublicGame, type SeatView } from "@playpop/finalfew";
import "@unfaithful/client/finalfew.css";
import "./ff-phone.css";

const SAVED = "finalfew.phone";

interface Saved {
  code: string;
  playerId: string;
  token: string;
  name: string;
}

function readSaved(): Saved | null {
  try {
    return JSON.parse(localStorage.getItem(SAVED) ?? "null") as Saved | null;
  } catch {
    return null;
  }
}

export function hasSavedFinalFew(): boolean {
  return !!readSaved();
}

function useSeat(initialCode: string) {
  const [view, setView] = useState<FFPlayerView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [restoring, setRestoring] = useState(() => !!readSaved());
  const socket = useRef<FFSocket | null>(null);

  useEffect(() => {
    const s = connectFinalFew();
    socket.current = s;
    const { clock, stop } = syncFinalFewClock(s);
    const t = setInterval(() => setOffset(clock.offset), 1000);
    s.on("ff:player", setView);
    s.on("ff:closed", (reason) => {
      localStorage.removeItem(SAVED);
      setView(null);
      setError(`${reason} Thanks for playing.`);
      setTimeout(() => (location.href = "/"), 2500);
    });
    s.on("connect", () => {
      const saved = readSaved();
      if (!saved || (initialCode && saved.code !== initialCode)) return setRestoring(false);
      ffRequest(s, "ff:rejoin", { code: saved.code, playerId: saved.playerId, token: saved.token })
        .catch(() => localStorage.removeItem(SAVED))
        .finally(() => setRestoring(false));
    });
    return () => {
      stop();
      clearInterval(t);
      s.disconnect();
    };
  }, [initialCode]);

  const call = async <T,>(p: Promise<T> | null | undefined): Promise<T | null> => {
    if (!p) return null;
    try {
      setError(null);
      return await p;
    } catch (e) {
      setError((e as Error).message);
      return null;
    }
  };
  const s = () => socket.current!;
  return {
    view,
    error,
    offset,
    restoring,
    clearError: () => setError(null),
    join: async (code: string, name: string) => {
      const r = await call(ffRequest(s(), "ff:join", { code, name }));
      if (r) localStorage.setItem(SAVED, JSON.stringify({ code, name, ...r }));
    },
    start: () => call(ffRequest(s(), "ff:start", {})),
    again: () => call(ffRequest(s(), "ff:again", {})),
    setMode: (mode: GameMode) => call(ffRequest(s(), "ff:settings", { mode })),
    answer: (value: string) => call(ffRequest(s(), "ff:answer", { value })),
    pass: () => call(ffRequest(s(), "ff:pass", {})),
    draft: (value: string) => void ffRequest(s(), "ff:draft", { value }).catch(() => undefined),
    walk: (walk: boolean) => call(ffRequest(s(), "ff:walk", { walk })),
    final: (choice: "share" | "play") => call(ffRequest(s(), "ff:final", { choice })),
    leave: async () => {
      await ffRequest(s(), "ff:leave", {}).catch(() => undefined);
      localStorage.removeItem(SAVED);
      location.href = "/";
    },
    end: async () => {
      await ffRequest(s(), "ff:end", {}).catch(() => undefined);
      localStorage.removeItem(SAVED);
      location.href = "/";
    },
  };
}

type Seat = ReturnType<typeof useSeat>;

function useNow(offset: number): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  return now + offset;
}

/** THE FINAL FEW on a phone: your answer pad, and the question too, for anyone not in front of the TV. */
export function FFPhone({ code }: { code: string }) {
  const seat = useSeat(code);
  const now = useNow(seat.offset);
  useEffect(() => {
    document.title = "The Final Few";
  }, []);
  const v = seat.view;
  return (
    <div className="ff ff-phone">
      <main className="ffp">
        <header className="ffp-top">
          <span className="ff-display ffp-brand">The Final Few</span>
          {v?.game && v.game.rung !== null && v.game.stage !== "over" && <span className="ffp-rung ff-display">{v.game.rung}%</span>}
        </header>
        {!v ? (
          seat.restoring ? <p className="ff-muted">Finding your seat…</p> : <Join code={code} seat={seat} />
        ) : v.phase === "lobby" || !v.game ? (
          <Waiting view={v} seat={seat} />
        ) : (
          <Playing view={v} game={v.game} me={v.seat} seat={seat} now={now} />
        )}
        {v && v.phase !== "lobby" && (
          <footer className="ffp-exit">
            {v.me.isHost ? (
              <button className="ffp-exit-btn" onClick={() => confirm("End this game for everyone? It can't be undone.") && void seat.end()}>
                End the game for everyone
              </button>
            ) : (
              <button className="ffp-exit-btn" onClick={() => confirm("Leave this game? You can't rejoin it.") && void seat.leave()}>
                Leave the game
              </button>
            )}
          </footer>
        )}
        {seat.error && (
          <p className="ffp-error" role="alert" onClick={seat.clearError}>
            {seat.error}
          </p>
        )}
      </main>
    </div>
  );
}

function Join({ code, seat }: { code: string; seat: Seat }) {
  const [name, setName] = useState(() => readSaved()?.name ?? "");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    await seat.join(code, name);
    setBusy(false);
  };
  return (
    <form className="ffp-join" onSubmit={submit}>
      <p className="ffp-lede">You're joining game {code}.</p>
      <label className="ffp-label" htmlFor="ffp-name">
        Your name
      </label>
      <input id="ffp-name" className="ffp-input" value={name} maxLength={20} autoComplete="given-name" onChange={(e) => setName(e.target.value)} />
      <button className="ff-button" disabled={!name.trim() || busy}>
        {busy ? "Joining…" : "Take my seat"}
      </button>
    </form>
  );
}

function Waiting({ view, seat }: { view: FFPlayerView; seat: Seat }) {
  return (
    <section className="ffp-wait">
      <p className="ffp-big ff-display">You're in, {view.me.name}.</p>
      <p className="ffp-lede">
        {view.humans.length === 1 ? "Just you so far." : `${view.humans.length} people here.`} AI players fill the rest of the {RULES.seats} seats.
      </p>
      {view.me.isHost ? (
        <>
          <div className="ffp-modes">
            {(["standard", "relaxed", "rollover"] as const).map((m) => (
              <button key={m} className={`ffp-mode ${view.settings.mode === m ? "is-on" : ""}`} aria-pressed={view.settings.mode === m} onClick={() => void seat.setMode(m)}>
                {m === "standard" ? "Standard" : m === "relaxed" ? "Relaxed (45s)" : "Rollover"}
              </button>
            ))}
          </div>
          <button className="ff-button" onClick={() => void seat.start()}>
            Start the game
          </button>
        </>
      ) : (
        <p className="ff-muted">Waiting for the host to start.</p>
      )}
      <button className="ff-button is-quiet" onClick={() => void seat.leave()}>
        Leave
      </button>
    </section>
  );
}

function Playing({ view, game, me, seat, now }: { view: FFPlayerView; game: PublicGame; me: SeatView | null; seat: Seat; now: number }) {
  if (!me) return <p className="ff-muted">Watching.</p>;
  const still = me.state === "active" || me.state === "finalist";
  switch (game.stage) {
    case "lobby":
      return (
        <section className="ffp-wait">
          <p className="ffp-big ff-display">Get ready.</p>
          <p className="ffp-lede">
            You have {pounds(RULES.stake)}. Fifteen questions, {LADDER[0]}% down to {LADDER[LADDER.length - 1]}%. A wrong answer, or no answer, and you're out.
          </p>
        </section>
      );
    case "question":
      return <QuestionPad game={game} me={me} seat={seat} now={now} />;
    case "reveal": {
      if (game.reveal && me.right !== null) {
        const passed = me.passed;
        return (
          <section className={`ffp-verdict ${me.right ? "is-right" : passed ? "is-passed" : game.forFun ? "is-passed" : "is-out"}`}>
            <p className="ffp-big ff-display">{me.right ? "✓ Right" : passed ? "↷ Passed" : game.forFun ? "✕ Wrong, but it was for fun" : "✕ You're out"}</p>
            {!me.right && !passed && <p className="ffp-lede">{me.myAnswer ? `You said ${me.myAnswer}.` : "No answer reached us before the buzzer."}</p>}
            <p className="ffp-lede">The answer: {game.reveal.answer}</p>
            <p className="ff-muted">{game.reveal.explain}</p>
          </section>
        );
      }
      return (
        <section className="ffp-wait">
          {game.reveal && <p className="ffp-lede">The answer: {game.reveal.answer}</p>}
          <p className="ff-muted">{still ? "Next question coming up." : "You're out, but you can keep playing along."}</p>
          <p className="ffp-pot">Pot: {pounds(game.pot)}</p>
        </section>
      );
    }
    case "walk-offer":
      return me.canWalk ? (
        <section className="ffp-offer">
          <p className="ffp-big ff-display">Walk away with {pounds(RULES.stake)}?</p>
          <p className="ffp-lede">Or play on for a share of the pot. You can change your mind until the time runs out.</p>
          <div className="ffp-choices">
            <button className={`ff-button ${me.walking ? "" : "is-quiet"}`} aria-pressed={!!me.walking} onClick={() => void seat.walk(true)}>
              Keep {pounds(RULES.stake)}
            </button>
            <button className={`ff-button ${me.walking ? "is-quiet" : ""}`} aria-pressed={!me.walking} onClick={() => void seat.walk(false)}>
              Play on
            </button>
          </div>
          <Countdown to={game.deadline} now={now} />
        </section>
      ) : (
        <p className="ff-muted">Some players can walk away now. {me.passUsedOn ? "You've used your pass, so you play on." : ""}</p>
      );
    case "final-offer":
      return me.state === "finalist" ? (
        <section className="ffp-offer">
          <p className="ffp-big ff-display">You're in the final.</p>
          <p className="ffp-lede">
            Take a share of {pounds(RULES.shareFund)}, or answer the 1% question for a share of {pounds(game.pot)}. Nobody sees your choice until everyone's chosen.
          </p>
          <div className="ffp-choices">
            <button className={`ff-button ${me.finalChoice === "share" ? "" : "is-quiet"}`} aria-pressed={me.finalChoice === "share"} onClick={() => void seat.final("share")}>
              Take the share
            </button>
            <button className={`ff-button ${me.finalChoice === "play" ? "" : "is-quiet"}`} aria-pressed={me.finalChoice === "play"} onClick={() => void seat.final("play")}>
              Play
            </button>
          </div>
          <Countdown to={game.deadline} now={now} />
        </section>
      ) : (
        <p className="ff-muted">The finalists are choosing.</p>
      );
    case "over":
      return (
        <section className="ffp-wait">
          <p className="ffp-big ff-display">{me.winnings > 0 ? `You won ${pounds(me.winnings)}` : "Better luck next time"}</p>
          <p className="ff-muted">In-game money only. Bragging rights are real.</p>
          {view.me.isHost && (
            <button className="ff-button" onClick={() => void seat.again()}>
              Play again
            </button>
          )}
        </section>
      );
  }
}

function Countdown({ to, now }: { to: number | null; now: number }) {
  if (!to) return null;
  return <p className="ffp-count ff-display">{Math.max(0, Math.ceil((to - now) / 1000))}</p>;
}

function QuestionPad({ game, me, seat, now }: { game: PublicGame; me: SeatView; seat: Seat; now: number }) {
  const q = game.question!;
  const answering = me.state === "active" || (me.state === "finalist" && me.finalChoice === "play");
  const [value, setValue] = useState("");
  const [ghost, setGhost] = useState<string | null>(null);
  useEffect(() => {
    setValue("");
    setGhost(null);
  }, [q.id]);
  // Keep the server up to date with what's typed: if time runs out before Lock in, it still counts.
  const drafted = useRef("");
  const sendDraft = useRef(seat.draft);
  sendDraft.current = seat.draft;
  useEffect(() => {
    if (!answering || me.myAnswer !== null || me.passed || value === drafted.current) return;
    const t = setTimeout(() => {
      drafted.current = value;
      sendDraft.current(value);
    }, 250);
    return () => clearTimeout(t);
  }, [value, answering, me.myAnswer, me.passed]);
  const left = game.deadline ? Math.max(0, Math.ceil((game.deadline - now) / 1000)) : 0;
  const locked = me.myAnswer !== null || me.passed;
  const submit = () => {
    if (!value.trim()) return;
    if (answering) void seat.answer(value.trim());
    else setGhost(value.trim());
  };

  return (
    <section className="ffp-question">
      <div className="ffp-q-meta">
        <span>{game.forFun ? "For fun" : answering ? "Your answer" : "Playing along"}</span>
        <span className="ffp-clock ff-display">{left}</span>
      </div>
      <p className="ffp-prompt">{q.prompt}</p>
      {q.media && <div className="ffp-media" dangerouslySetInnerHTML={{ __html: q.media }} />}
      {locked ? (
        <p className="ffp-locked ff-display">{me.passed ? "↷ Passed" : `Locked: ${display(q.format, me.myAnswer!, q.options)}`}</p>
      ) : ghost ? (
        <p className="ffp-locked ff-display">Your guess: {display(q.format, ghost, q.options)}</p>
      ) : (
        <>
          <Pad format={q.format} options={q.options} value={value} onChange={setValue} />
          <button className="ff-button ffp-lock" disabled={!value.trim()} onClick={submit}>
            {answering ? "Lock in" : "Guess for fun"}
          </button>
          {me.canPass && (
            <button className="ff-button is-danger" onClick={() => void seat.pass()}>
              Pass: put my {pounds(RULES.stake)} in the pot
            </button>
          )}
        </>
      )}
    </section>
  );
}

const display = (format: string, value: string, options: string[] | null) => {
  if (format === "mc" && options) {
    const i = "ABCDEF".indexOf(value.toUpperCase());
    return i >= 0 && options[i] && options[i] !== value ? `${value.toUpperCase()}: ${options[i]}` : value.toUpperCase();
  }
  return value;
};

function Pad({ format, options, value, onChange }: { format: string; options: string[] | null; value: string; onChange: (v: string) => void }) {
  if (format === "mc" && options) {
    return (
      <ol className="ffp-options">
        {options.map((o, i) => {
          const letter = "ABCDEF"[i]!;
          return (
            <li key={i}>
              <button className={`ffp-option ${value === letter ? "is-on" : ""}`} aria-pressed={value === letter} onClick={() => onChange(letter)}>
                <span className="ffp-letter">{letter}</span>
                {o !== letter && <span>{o}</span>}
              </button>
            </li>
          );
        })}
      </ol>
    );
  }
  if (format === "number") {
    return (
      <div className="ffp-keypad">
        <p className="ffp-entry ff-display" aria-live="polite">
          {value || " "}
        </p>
        <div className="ffp-keys">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9", "−", "0", "⌫"].map((k) => (
            <button
              key={k}
              className="ffp-key"
              aria-label={k === "⌫" ? "Delete" : k === "−" ? "Minus" : k}
              onClick={() => onChange(k === "⌫" ? value.slice(0, -1) : k === "−" ? (value.startsWith("-") ? value.slice(1) : `-${value}`) : (value + k).slice(0, 9))}
            >
              {k}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (format === "letter") {
    return (
      <div className="ffp-letters">
        {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((l) => (
          <button key={l} className={`ffp-key ${value === l ? "is-on" : ""}`} aria-pressed={value === l} onClick={() => onChange(l)}>
            {l}
          </button>
        ))}
      </div>
    );
  }
  return (
    <input
      className="ffp-input"
      value={value}
      maxLength={40}
      autoCapitalize={format === "grid" ? "characters" : "off"}
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      placeholder={format === "grid" ? "e.g. B3" : "Type your answer"}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
